import React from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import withStore from '../hocs/withStore';
import getInjectors from './sagaInjectors';

/**
 * Dynamically injects a saga, passes component's props as saga arguments
 *
 * @param {string} key A key of the saga
 * @param {function} saga A root saga that will be injected
 * @param {string} [mode] By default (constants.RESTART_ON_REMOUNT) the saga will be started on component mount and
 * cancelled with `task.cancel()` on component un-mount for improved performance. Another two options:
 *   - constants.DAEMON—starts the saga on component mount and never cancels it or starts again,
 *   - constants.ONCE_TILL_UNMOUNT—behaves like 'RESTART_ON_REMOUNT' but never runs it again.
 *
 */
export default ({ key, saga, mode }) =>
  (WrappedComponent) => {
    class InjectSaga extends React.Component {
      state = {
        ready: false,
      };

      owner = Symbol(key);

      static WrappedComponent = WrappedComponent;

      static propTypes = {
        reduxStore: PropTypes.object.isRequired,
      };

      static displayName = `withSaga(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

      componentDidMount() {
        this.injectors ||= getInjectors(this.props.reduxStore);
        const { reduxStore, ...props } = this.props;
        this.injectors.injectSaga(key, { saga, mode }, props, { owner: this.owner });

        // Mount the wrapped subtree only after the watcher has been committed.
        if (!this.state.ready) this.setState({ ready: true });
      }

      componentWillUnmount() {
        this.injectors?.ejectSaga(key, { owner: this.owner });
      }

      render() {
        if (!this.state.ready) return null;
        const { reduxStore, ...props } = this.props;
        return <WrappedComponent {...props} />;
      }
    }

    return hoistNonReactStatics(withStore(InjectSaga), WrappedComponent);
  };
